import {
    Box,
    Checkbox,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid2,
    IconButton,
    Tooltip,
    Typography
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';

import { SharedCard } from '../../../../components/shared-card/card';
import EditableField from './EditableField';
import ProfileForm from './profileForm';
import Snackbar from '@mui/material/Snackbar';
import { useState } from 'react';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';

const SectionHeader = ({ icon: Icon, children }) => (
    <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Icon fontSize="small" color="primary" />
        <Typography variant="h6">{children}</Typography>
    </Box>
);


export default function PersonalDetails({ user }) {
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [snackSeverity, setSnackSeverity] = useState('');

    const { t } = useTranslation();


    const loanTypes = {
        'personal' : t('loan_types.personal'),
        'business' : t('loan_types.business'),  // ! legacy
        'property_u_25l' : t('loan_types.property_u_25l'),
        'property_b_25l_50l' : t('loan_types.property_b_25l_50l'),
        'property_o_50l' : t('loan_types.property_a_50l'),
        'unsecured_business' : t('loan_types.unsecured_business'),
        'home' : t('loan_types.home'),
    }

    const handleClose = () => {
      setShowSnackbar(false);
      setSnackBarMessage('');
      setSnackSeverity('');
    };

    const handleShare = (event) => {
        event.preventDefault();
        const messageText = `Hi👋, Welcome to Optimo Capital, for all your Loan needs! You are referred by ${user.user_profile.first_name} ${user.user_profile.last_name}.\n\nClick on this link and apply now - ${user.user_profile.referrals_made[0]?.invite_url}`;
        navigator.clipboard.writeText(messageText)
            .then(() => {
                setShowSnackbar(true);
                setSnackBarMessage('Referral Link Copied');
                setSnackSeverity('success');
            })
            .catch(err => {
                console.error('Could not copy text: ', err);
            });
        // Properly encode the message for URL parameters, including newlines
        const encodedMessage = encodeURIComponent(messageText);
        let url = `https://api.whatsapp.com/send/?text=${encodedMessage}`;
        window.open(url, '_blank');
    };


    // SharedCard is a fixed 450px; the profile is one record, so it runs the
    // width of the container with its sections separated by rules instead.
    // '&&' doubles the specificity: SharedCard pins width to 450px inside an
    // sm breakpoint, which a plain sx width loses to.
    const cardSx = {
        '&&': { width: '100%', maxWidth: 'none' },
        m: 0,
        textAlign: 'left',
        gap: 3,
        p: 4,
        boxShadow: 'none',
        border: '1px solid',
        borderColor: 'divider',
    };

    const user_address = user.user_profile.user_address
    const display_address = [
        [user_address?.house, user_address?.loc].filter(Boolean).join(' '),
        user_address?.vtc,
        user_address?.dist,
        user_address?.state,
        user_address?.zip_code,
    ].filter(Boolean).join(', ');

    const loanTypeList = user.user_profile?.loan_type ?? [];
    const phoneNumber = user.phone_number;
    const emailAddress = user.user_profile?.email;
    const dateOfBirth = user?.kyc_details?.dateofbirth;

    // date_joined is the account itself; formatDate carries a time, which is
    // noise for a "member since" line.
    const memberSince = user.date_joined
        ? new Date(user.date_joined).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
        : null;
    const referral = user.user_profile?.referrals_made?.[0];

    return (
        <>
            <Snackbar open={showSnackbar} autoHideDuration={3000} onClose={handleClose}>
                <Alert onClose={handleClose} severity={snackSeverity} sx={{ width: '100%' }}>
                <Typography>{snackBarMessage}</Typography>
                </Alert>
            </Snackbar>
            <ProfileForm>
                {(isEditable) => (
                    <SharedCard sx={cardSx}>
                        <Box>
                            <SectionHeader icon={PersonOutlineIcon}>
                                {t('user_profile.personal_details.header')}
                            </SectionHeader>

                            <Grid2 container spacing={2.5}>
                                <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                    <EditableField
                                        label={t('name')}
                                        value={`${user.user_profile.first_name} ${user.user_profile.last_name}`}
                                        isEditable={isEditable}
                                    />
                                </Grid2>

                                {dateOfBirth && (
                                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                        <EditableField
                                            label={t('date_of_birth')}
                                            value={dateOfBirth}
                                            isEditable={isEditable}
                                        />
                                    </Grid2>
                                )}

                                {memberSince && (
                                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                        <EditableField
                                            label={t('user_profile.personal_details.member_since', { defaultValue: 'Member Since' })}
                                            value={memberSince}
                                            isEditable={false}
                                        />
                                    </Grid2>
                                )}

                                {referral?.referral_code && (
                                    <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                        <Box display="flex" alignItems="flex-end" gap={0.5}>
                                            <EditableField
                                                label={t('user_profile.personal_details.referral_code')}
                                                value={referral.referral_code}
                                                isEditable={isEditable}
                                            />

                                            {referral?.invite_url && (
                                                <Tooltip title={t('user_profile.personal_details.click_to_share')}>
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={handleShare}
                                                        aria-label={t('user_profile.personal_details.click_to_share')}
                                                    >
                                                        <ShareIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid2>
                                )}

                                {display_address && (
                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                        <EditableField
                                            label={t('address')}
                                            value={display_address}
                                            isEditable={isEditable}
                                            multiline
                                        />
                                    </Grid2>
                                )}

                            </Grid2>
                        </Box>

                        {(phoneNumber || emailAddress) && (
                            <>
                                <Divider />

                                <Box>
                                    <SectionHeader icon={ContactMailOutlinedIcon}>
                                        {t('user_profile.contact_details.header')}
                                    </SectionHeader>

                                    <Grid2 container spacing={2.5}>
                                        {phoneNumber && (
                                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                                <EditableField
                                                    label={t('phone_number.label')}
                                                    value={phoneNumber}
                                                    isEditable={isEditable}
                                                />
                                            </Grid2>
                                        )}
                                        {emailAddress && (
                                            <Grid2 size={{ xs: 12, sm: 6, md: 4 }}>
                                                <EditableField
                                                    label={t('email_address.label')}
                                                    value={emailAddress}
                                                    isEditable={isEditable}
                                                />
                                            </Grid2>
                                        )}
                                    </Grid2>
                                </Box>
                            </>
                        )}

                        {loanTypeList.length > 0 && (
                            <>
                                <Divider />

                                <Box>
                                    <SectionHeader icon={AccountBalanceOutlinedIcon}>
                                        {t('loan_types.label')}
                                    </SectionHeader>

                                    <FormGroup row sx={{ gap: 3 }}>
                                        {loanTypeList.map((value) => (
                                            <FormControlLabel
                                                key={value}
                                                label={loanTypes[value]}
                                                control={<Checkbox checked />}
                                            />
                                        ))}
                                    </FormGroup>
                                </Box>
                            </>
                        )}
                    </SharedCard>
                )}
            </ProfileForm>
        </>
    );
}
